// Copyright © Luware Solutions AG. All rights reserved.

import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { IMenuFilter } from '@nimbus-library/features/menu';
import { PortalPage } from '@nimbus-global/features/ui-profile/models/portal-page.enum';
import { UiProfilePortalState } from '@nimbus-portal/features/ui-profile/store/ui-profile-portal.state';

/**
 * Menu filter that hides pages not included in the user's assigned UI profile.
 * Returns true (show) when no profile is assigned or the page is in the profile's visiblePages.
 * Crucially, the filter waits for `loaded === true` before emitting its first value: otherwise
 * the menu would render with the full default set and then remove items once the profile resolves,
 * producing a visible flash for users with a restrictive profile.
 */
export class UIProfileFilter implements IMenuFilter<PortalPage> {
  public constructor(private readonly store: Store) {}

  public static for(page: PortalPage): { type: typeof UIProfileFilter; option: PortalPage } {
    return { type: UIProfileFilter, option: page };
  }

  public validate$(page: PortalPage): Observable<boolean> {
    return this.store.select(UiProfilePortalState.loaded).pipe(
      filter((loaded) => !!loaded),
      take(1),
      switchMap(() =>
        this.store
          .select(UiProfilePortalState.visiblePages)
          .pipe(map((visiblePages) => visiblePages === null || visiblePages.includes(page))),
      ),
    );
  }
}
